<template>
  <div class="text-center">
    <div v-if="!safetyAssistant" class="welcome-message">
      <i18n path="login.title" tag="p">
        <template #brand><b>SALUS</b></template>
      </i18n>
    </div>
    <div v-else-if="site?.name" class="welcome-message">
      <p>
        {{
          $t('login.welcomeMessage', {
            target:
              qrType === 'workflow'
                ? $t('login.orientationForJobSite')
                : $t('login.jobSite'),
          })
        }}
      </p>
      <h2>{{ site.name }}</h2>
    </div>

    <p v-if="notFound" class="identifier mt-2 mb-8 darkRed--text">
      <span class="block">{{ $t('userIdentifier.notFound') }}</span>
      <span class="block">{{ phoneIdentifier || emailIdentifier }}</span>
    </p>
    <p v-if="message" class="mb-5 darkRed--text">{{ message }}</p>

    <PhoneNumberInput
      v-show="showPhone"
      ref="phoneInput"
      v-model="phoneValue"
      sls-element-id="login-input-phone"
      @enter="discoverUser"
    />

    <v-text-field
      v-show="!showPhone"
      v-model="emailValue"
      :label="$t('common.email')"
      class="login-input-email"
      dense
      inputmode="email"
      outlined
      sls-element-id="login-input-email"
      @keydown.enter="discoverUser"
    />

    <SecondaryButton
      v-if="!safetyAssistant"
      :disabled="loading"
      class="mb-6"
      sls-element-id="login-button-form-next"
      @click="toggleIdentifier"
    >
      {{ showPhone ? $t('login.useEmail') : $t('login.usePhone') }}
    </SecondaryButton>

    <PrimaryButton
      :click="discoverUser"
      :disabled="loading"
      class="mb-6"
      sls-element-id="login-button-form-next"
    >
      {{ $t('common.next') }}
    </PrimaryButton>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import PhoneNumberInput from '@/components/common/PhoneNumberInput'
import PrimaryButton from '@/components/common/PrimaryButton'
import SecondaryButton from '@/components/common/SecondaryButton'

export default {
  name: 'UserIdentifier',
  components: {
    PhoneNumberInput,
    PrimaryButton,
    SecondaryButton,
  },
  props: {
    loginHint: {
      default: () => ({ type: null, value: null }),
      type: Object,
    },
  },
  data() {
    return {
      errorMessage: null,
      emailValue: this.loginHint.type === 'email' ? this.loginHint.value : null,
      phoneValue: this.loginHint.type === 'phone' ? this.loginHint.value : null,
    }
  },
  computed: {
    ...mapState({
      emailIdentifier: 'emailIdentifier',
      loading: 'loading',
      message: 'message',
      notFound: 'notFound',
      phoneIdentifier: 'phoneIdentifier',
      showIdentifier: 'showIdentifier',
      stage: 'stage',
    }),
    ...mapGetters({
      qrType: 'qrType',
      site: 'site',
      safetyAssistant: 'safetyAssistant',
    }),
    showPhone() {
      return this.showIdentifier === 'phone' && !this.safetyAssistant
    },
    identifier() {
      return this.showPhone ? this.phoneValue : this.emailValue
    },
  },
  watch: {
    stage(value) {
      // If we are back to the user identifier stage, reset the phone input
      if (value === 'userIdentifier') {
        this.resetIdentifier()
      }
    },
    emailIdentifier(value) {
      this.emailValue = value
    },
    phoneIdentifier(value) {
      this.phoneValue = value
    },
  },
  methods: {
    ...mapActions({
      discover: 'discover',
      resetState: 'resetState',
      setShowIdentifier: 'setShowIdentifier',
    }),
    async discoverUser() {
      await this.discover(this.identifier)
    },
    reset() {
      this.resetState()
      this.resetIdentifier()
    },
    resetIdentifier() {
      this.$refs.phoneInput.phoneNumber = ''
      this.$refs.phoneInput.selectedCountry = '+1'
      this.phoneValue = null
      this.emailValue = null
    },
    toggleIdentifier() {
      const identifier = this.showPhone ? 'email' : 'phone'
      this.setShowIdentifier(identifier)
      this.reset()
    },
  },
}
</script>
<style lang="sass">
.login-input-email
  .v-input__slot
    padding: 4px 12px !important
.create-account
  font-size: 1rem
  color: $black
  cursor: pointer
  span
    color: $darkBlue
  &:hover
    font-weight: 500
.identifier
  font-size: 1rem
  font-weight: 500
  .block
    display: block
.welcome-message
  background-color: var(--v-lightBlue-base)
  margin-bottom: 24px
  padding: 8px 32px
  p,
  h2
    margin: 0
  h2
    font-size: 1.25rem
    font-weight: 700
    .login-container
  .welcome-message
    color: $black
.theme--dark
  .login-container
    .welcome-message
      color: $white
.theme--dark
  .login-input-email
    .v-input__control
      .v-input__slot
        background-color: $darkBackground !important
        fieldset
          border-color: $darkBorder !important
      input
        color: $white !important
</style>
